'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'

interface SEOData {
  id: string
  page: string
  title: string
  description: string
  keywords: string
  noindex: boolean
}

interface BrandConfig {
  NAME: string
  SHORT_NAME: string
  TITLE: string
  DESCRIPTION: string
  COPYRIGHT: string
}

interface UseSEOResult {
  pageTitle: string
  pageDescription: string
  keywords: string
  isLoading: boolean
  brandConfig: BrandConfig | null
}

export function useSEO(): UseSEOResult {
  const pathname = usePathname()
  const [seoData, setSeoData] = useState<SEOData | null>(null)
  const [brandConfig, setBrandConfig] = useState<BrandConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadSEOData()
  }, [pathname])

  const loadSEOData = async () => {
    try {
      // Add cache busting with timestamp
      const timestamp = Date.now()
      const response = await fetch(`/api/seo/data?t=${timestamp}`)
      if (response.ok) {
        const data = await response.json()
        
        // Tìm SEO data cho trang hiện tại
        const currentPageSEO = data.pages?.find((page: SEOData) => page.page === pathname)
        setSeoData(currentPageSEO || null)
        setBrandConfig(data.brand || null)
      }
    } catch (error) {
      console.error('Failed to load SEO data:', error)
    }
    setIsLoading(false)
  }

  // Fallback titles và descriptions nếu không có trong admin
  const getFallbackData = () => {
    const fallbacks: { [key: string]: { title: string; description: string } } = {
      '/': {
        title: 'Trực tiếp bóng đá hôm nay',
        description: 'Xem trực tiếp bóng đá miễn phí chất lượng HD'
      },
      '/tin-bong-da': {
        title: 'Tin tức bóng đá hôm nay: bình luận, chuyển nhượng, kết quả',
        description: 'Tổng hợp tin tức bóng đá hôm nay mới nhất: cập nhật tình hình chuyển nhượng, kết quả và bình luận sau trận đầy đủ từ các giải đấu hot nhất.'
      },
      '/kqbd': {
        title: 'Kết quả bóng đá hôm nay: cập nhật tỷ số trận đấu mới nhất',
        description: 'Kết quả bóng đá hôm nay nhanh và chính xác nhất. Cập nhật tỷ số trực tiếp các trận đấu từ Premier League, La Liga, Champions League.'
      },
      '/highlights': {
        title: 'Highlights bóng đá: video tóm tắt trận đấu hot nhất',
        description: 'Xem highlights bóng đá chất lượng HD, tóm tắt những pha bóng đẹp nhất từ các trận đấu hàng đầu thế giới.'
      },
      '/lich-thi-dau-bong-da': {
        title: 'Lịch thi đấu bóng đá hôm nay và tuần này',
        description: 'Lịch thi đấu bóng đá đầy đủ và chính xác nhất. Cập nhật lịch đá bóng hôm nay và những ngày tới.'
      },
      '/nhan-dinh-bong-da': {
        title: 'Nhận định bóng đá hôm nay: soi kèo chuyên sâu, dự đoán kết quả chính xác',
        description: 'Nhận định bóng đá chính xác từ các chuyên gia hàng đầu. Soi kèo chi tiết, phân tích sâu về phong độ đội hình, lịch sử đối đầu và dự đoán tỷ số cho các trận đấu hot nhất.'
      }
    }

    return fallbacks[pathname] || { title: '', description: '' }
  }

  const fallbackData = getFallbackData()
  
  return {
    pageTitle: seoData?.title || fallbackData.title,
    pageDescription: seoData?.description || fallbackData.description,
    keywords: seoData?.keywords || '',
    isLoading,
    brandConfig
  }
} 